"use client";

import Image, { StaticImageData } from "next/image";
import DOMPurify from "isomorphic-dompurify";

type BlogHeroProps = {
  title: string;
  category: string;
  date: any;
  imageSrc: string | StaticImageData;
};

const PREFERRED_SOURCE_URL =
  "https://www.google.com/preferences/source?q=konstantinfo.com";

export default function BlogHero({
  title,
  category,
  date,
  imageSrc,
}: BlogHeroProps) {
  const handlePreferredSource = () => {
    window.open(PREFERRED_SOURCE_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col gap-4 md:gap-5">
      <h1
        className="self-stretch justify-start text-[22px] lg:text-[length:var(--section-title)] font-medium clash-font leading-[120%] mt-8 lg:mt-0"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(title),
        }}
      />

      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <span className="inline-flex items-center justify-center rounded-[45px] bg-[#ECF0F6] px-4 md:px-5 py-1.5 md:py-2 text-[14px] md:text-[16px] font-medium text-[#00040b]">
          {category}
        </span>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[16px] px-3 md:text-[18px] text-[#636364] font-medium">
            Published On: {date?.publishedDate}
          </span>

          <span className="hidden md:block h-5 w-px bg-[#c1cde0]" />

          <span className="text-[16px] px-3 md:text-[18px] text-[#636364] font-medium">
            Last Updated: {date?.date}
          </span>
        </div>
        <div className="preferred-btn">
          <button
            type="button"
            rel="nofollow"
            onClick={handlePreferredSource}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-[#dadce0] bg-white px-4 text-[14px] font-medium text-[#202124] transition cursor-pointer hover:bg-[#f8fafd] focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/30"
            aria-label="Add this website to preferred sources"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M21.35 12.27c0-.78-.07-1.53-.22-2.27H12v4.3h5.24a4.48 4.48 0 0 1-1.94 2.94v2.45h3.14c1.84-1.69 2.91-4.18 2.91-7.42Z"
              />
              <path
                fill="#34A853"
                d="M12 21.92c2.63 0 4.84-.87 6.45-2.35l-3.14-2.45c-.87.58-1.98.92-3.31.92-2.54 0-4.69-1.72-5.46-4.03H3.3v2.53A9.75 9.75 0 0 0 12 21.92Z"
              />
              <path
                fill="#FBBC05"
                d="M6.54 14.01A5.86 5.86 0 0 1 6.23 12c0-.7.12-1.38.31-2.01V7.46H3.3A9.75 9.75 0 0 0 2.25 12c0 1.57.38 3.05 1.05 4.54l3.24-2.53Z"
              />
              <path
                fill="#EA4335"
                d="M12 5.96c1.43 0 2.71.49 3.72 1.45l2.79-2.79C16.83 3.04 14.63 2.08 12 2.08a9.75 9.75 0 0 0-8.7 5.38l3.24 2.53C6.31 7.68 8.46 5.96 12 5.96Z"
              />
            </svg>

            <span>Add to Preferred Sources</span>
          </button>
        </div>
      </div>

      <div className="relative h-auto w-full overflow-hidden rounded">
        <Image
          src={imageSrc}
          alt={title}
          width={1000}
          height={1000}
          priority
          fetchPriority="high"
          className="h-auto w-full"
        />
      </div>
    </div>
  );
}
